import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { InfoIcon, SaveIcon, RotateCcwIcon } from "lucide-react";
import { Tooltip } from "../ui/tooltip";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { usePermissions } from "../../PermissionsProvider";

const BillingPreferences: React.FC = () => {
  const { apiKey } = usePermissions();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCompany();
  }, [apiKey]);

  const fetchCompany = async () => {
    setLoading(true);
    CompanyService.getCompany(setLoading, apiKey).subscribe(
      (company: Company) => {
        setCompany(company);
      },
      (error: any) => {
        console.error(`Failed to fetch company data: ${error}`);
        setLoading(false);
      }
    );
  };

  const renderLabel = (label: string, tooltip: string) => (
    <div className="flex items-center gap-2">
      <Label className="text-sm font-gc-med text-foreground">{label}</Label>
      <Tooltip content={tooltip}>
        <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
      </Tooltip>
    </div>
  );

  const handleSave = () => {
    setSaving(true);
    // Implement save functionality here
    setTimeout(() => {
      setSaving(false);
    }, 1000);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Billing Preferences</h3>
          <p className="text-sm text-muted-foreground">Manage your billing information and preferences</p>
        </div>
        
        <Card className="border-border">
          <CardContent className="p-6 space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">Billing Preferences</h3>
        <p className="text-sm text-muted-foreground">
          Changes to these preferences will apply to future invoices only. If you
          need a past invoice reissued, please contact sales@trymaitai.ai.
        </p>
      </div>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-gc-med text-foreground">Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              {renderLabel(
                "Company name",
                "If specified, this name will appear on invoices instead of your organization name."
              )}
              <Input
                value={company?.company_name || ""}
                onChange={(e) =>
                  setCompany((prev) =>
                    prev ? { ...prev, company_name: e.target.value } : null
                  )
                }
                placeholder="Enter company name"
              />
            </div>

            <div className="space-y-3">
              {renderLabel(
                "Purchase order (PO) number",
                "Your PO number will be displayed on future invoices."
              )}
              <Input
                value={(company?.meta?.["po_number"] as string) || ""}
                onChange={(e) =>
                  setCompany((prev) =>
                    prev
                      ? {
                          ...prev,
                          meta: { ...prev.meta, po_number: e.target.value },
                        }
                      : null
                  )
                }
                placeholder="Enter PO number"
              />
            </div>

            <div className="space-y-3 md:col-span-2">
              {renderLabel(
                "Billing email",
                "Invoices and other billing notifications will be sent here (in addition to being sent to the owners of your organization)."
              )}
              <Input
                type="email"
                value={(company?.meta?.["contact_email"] as string) || ""}
                onChange={(e) =>
                  setCompany((prev) =>
                    prev
                      ? {
                          ...prev,
                          meta: { ...prev.meta, contact_email: e.target.value },
                        }
                      : null
                  )
                }
                placeholder="Enter billing email"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-gc-med text-foreground">Primary Business Address</CardTitle>
          <p className="text-sm text-muted-foreground">
            This is the physical address of the company purchasing Maitai services and is used to calculate any applicable sales tax.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">Street address</Label>
            <Input
              value={(company?.meta?.["address"] as string) || ""}
              onChange={(e) =>
                setCompany((prev) =>
                  prev
                    ? {
                        ...prev,
                        meta: { ...prev.meta, address: e.target.value },
                      }
                    : null
                )
              }
              placeholder="Enter street address"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-3 md:col-span-2">
              <Label className="text-sm font-gc-med text-foreground">City</Label>
              <Input
                value={(company?.meta?.["city"] as string) || ""}
                onChange={(e) =>
                  setCompany((prev) =>
                    prev
                      ? {
                          ...prev,
                          meta: { ...prev.meta, city: e.target.value },
                        }
                      : null
                  )
                }
                placeholder="Enter city"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-gc-med text-foreground">ZIP code</Label>
              <Input
                value={(company?.meta?.["zip_code"] as string) || ""}
                onChange={(e) =>
                  setCompany((prev) =>
                    prev
                      ? {
                          ...prev,
                          meta: { ...prev.meta, zip_code: e.target.value },
                        }
                      : null
                  )
                }
                placeholder="ZIP code"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">State</Label>
            <Select
              value={(company?.meta?.["state"] as string) || ""}
              onValueChange={(value) =>
                setCompany((prev) =>
                  prev
                    ? { ...prev, meta: { ...prev.meta, state: value } }
                    : null
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="California">California</SelectItem>
                {/* Add more states as needed */}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-2"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          <SaveIcon className="w-4 h-4" />
          Save Changes
        </Button>
        <Button
          variant="outline"
          onClick={() => fetchCompany()}
          className="gap-2"
        >
          <RotateCcwIcon className="w-4 h-4" />
          Reset
        </Button>
      </div>
    </div>
  );
};

export default BillingPreferences;
